import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { adminUpdateCompanyContact } from "../../../api";
import { colors } from "../../../styles";

interface EditContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyId: string;
    companyName: string;
    contact?: string;
}

interface IEditContactForm {
    contact: string;
}

export default function EditContactModal(props: EditContactModalProps) {

    const queryClient = useQueryClient();
    const toast = useToast();

    const { register, handleSubmit, reset, watch, formState: { isValid } } = useForm<IEditContactForm>({
        mode: "onChange",
        defaultValues: {
            contact: props.contact ?? "",
        },
    });

    useEffect(() => {
        if (props.isOpen) {
            reset({ contact: props.contact ?? "" });
        }
    }, [props.isOpen, props.contact, reset]);

    const currentValue = watch("contact") ?? "";
    const originalValue = props.contact ?? "";
    const isChanged = currentValue.trim() !== originalValue.trim();

    const updateContactMutation = useMutation({
        mutationFn: adminUpdateCompanyContact,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
            props.onClose();
            toast({
                title: "연락처를 저장했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "연락처 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onSubmit = (data: IEditContactForm) => {
        updateContactMutation.mutate({
            companyId: props.companyId,
            contact: data.contact.trim(),
        });
    };

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>
                        {props.companyName || "-"}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody>
                    <Stack gap={3}>
                        <Text fontSize={14} fontWeight={400} color={colors.gray}>
                            연락처
                        </Text>
                        <Input
                            {...register("contact", { required: true, minLength: 1 })}
                            type="text"
                            placeholder="연락처를 입력해주세요."
                        />
                    </Stack>
                </ModalBody>
                <Stack marginTop={5} width={"100%"} padding={3}>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        isLoading={updateContactMutation.isPending}
                        isDisabled={!isValid || !isChanged}
                        width={"100%"}
                        backgroundColor={colors.blue}
                        color={colors.white}
                        borderRadius={10}
                        _hover={{ bg: colors.blue }}
                        boxShadow="sm"
                    >
                        {originalValue ? "수정" : "등록"}
                    </Button>
                </Stack>
            </ModalContent>
        </Modal>
    );
}
